/**
 * Hazari bot.
 *
 * - Following: when the trick holds points (or the bot plays last) it wins with the cheapest card that beats the current
 *   winner; when it cannot win it sheds the card that is worth least to the opponents (no points, lowest rank).
 * - Leading: leads a card that is certain to win (the highest unseen of its suit) while keeping the rest for later;
 *   otherwise it leads its lowest worthless card. NORMAL and above track which cards are still unseen.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { hazariCardPoints, hazariCheapestCard, type HazariAction, type HazariLegal, type HazariView } from './engine.js';

function unseenCards(view: HazariView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.cards);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (ctx.profile.memory >= 1 || i >= n - Math.max(1, Math.round(n * ctx.profile.memory))) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(buildDeck().filter((c) => !seen.has(c)));
}

const rank = (c: CardId) => parseCard(c).rank;

function chooseCard(view: HazariView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const trick = view.trick;

  if (trick.length === 0) {
    if (unseen) {
      // A card nobody left can beat is a sure trick: cash the most valuable one first.
      const sure = legal.filter((c) => {
        const suit = parseCard(c).suit;
        return ![...unseen].some((u) => parseCard(u).suit === suit && rank(u) > rank(c));
      });
      if (sure.length > 0) return [...sure].sort((a, b) => hazariCardPoints(b) - hazariCardPoints(a) || rank(b) - rank(a))[0]!;
    }
    // Otherwise lead low and worthless.
    return hazariCheapestCard(legal);
  }

  const lead = parseCard(trick[0]!.card).suit;
  const following = legal.every((c) => parseCard(c).suit === lead);
  const last = trick.length === 3;
  const trickPoints = trick.reduce((n, p) => n + hazariCardPoints(p.card), 0);
  if (following) {
    const winning = Math.max(...trick.filter((p) => parseCard(p.card).suit === lead).map((p) => rank(p.card)));
    const over = legal.filter((c) => rank(c) > winning);
    if (over.length > 0 && (trickPoints > 0 || last || view.tricks.length === 12)) {
      // Win cheaply: the lowest card that still beats the current winner (the last trick also carries the bonus).
      return [...over].sort((a, b) => rank(a) - rank(b))[0]!;
    }
    // Cannot win, or the trick is worthless: give away as little as possible.
    return hazariCheapestCard(legal);
  }

  // Void in the led suit: shed the cheapest card.
  return hazariCheapestCard(legal);
}

export const hazariBot: BotEngine<HazariView, HazariLegal, HazariAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const others = view.totals.filter((_, s) => s !== me);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (view.totals[me]! - avg) / 300));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'play') return Math.min(1, Math.max(0, legal.cards.reduce((n, c) => n + hazariCardPoints(c), 0) / 130));
    return 0;
  },

  handleTimeout(_view, legal) {
    if (legal.kind === 'play') return { type: 'play', card: hazariCheapestCard(legal.cards) };
    throw new Error('No legal action');
  },
};
